import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { InvoiceFormReceiver } from '../../../types/invoiceForm/invoiceFormReceiver'
import { XRechnungEInvoicingAddressForm } from '../../../types/xRechnungEInvoicingAddressForm'
import { XRechnungEInvoicingAddressFormSchema } from '../types/xRechnungEInvoicingAddressFormSchema'
import { getCustomerEInvoicingAddressValues } from '../utils/getCustomerEInvoicingAddressValues'
import { getXRechnungEInvoicingAddressFormDefaultValues } from '../utils/getXRechnungEInvoicingAddressFormDefaultValues'
import { getXRechnungEInvoicingAddressFormSchema } from '../utils/getXRechnungEInvoicingAddressFormSchema'

interface ContextState {
  countryId?: string
  isSubmitting: boolean
  receiverName?: string
  submitForm: () => void
  validate: () => Promise<boolean>
  xRechnungEInvoicingAddressForm: UseFormReturn<XRechnungEInvoicingAddressFormSchema>
}

const XRechnungEInvoicingAddressFormContext = createContext<ContextState | undefined>(undefined)

interface XRechnungEInvoicingAddressFormProviderProps {
  children?: ReactNode
  customer?: InvoiceFormReceiver | null
  isSubmitting: boolean
  onSubmit: (formData: XRechnungEInvoicingAddressForm) => void
}

export const XRechnungEInvoicingAddressFormContextProvider = ({
  children,
  customer,
  isSubmitting,
  onSubmit,
}: XRechnungEInvoicingAddressFormProviderProps): ReactElement => {
  const { t } = useTranslation()

  const customerEInvoicingAddressValues = useMemo(() => getCustomerEInvoicingAddressValues(customer), [customer])

  const countryId = customer?.countryId
  const receiverName = customer?.name

  const xRechnungEInvoicingAddressForm = useForm<XRechnungEInvoicingAddressFormSchema>({
    defaultValues: getXRechnungEInvoicingAddressFormDefaultValues(),
    validationSchema: getXRechnungEInvoicingAddressFormSchema(t, countryId),
  })

  const { Form, handleSubmit, reset: resetForm, trigger } = xRechnungEInvoicingAddressForm

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()
    return trigger()
  }, [handleSubmit, trigger])

  useEffect(() => {
    if (customerEInvoicingAddressValues) {
      resetForm(customerEInvoicingAddressValues)
    }
  }, [customerEInvoicingAddressValues, resetForm])

  const contextValue = useMemo(
    () => ({
      countryId,
      isSubmitting,
      receiverName,
      submitForm,
      validate: handleValidation,
      xRechnungEInvoicingAddressForm,
    }),
    [countryId, handleValidation, isSubmitting, receiverName, submitForm, xRechnungEInvoicingAddressForm],
  )

  return (
    <XRechnungEInvoicingAddressFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </XRechnungEInvoicingAddressFormContext.Provider>
  )
}

export const useXRechnungEInvoicingAddressForm = () => {
  const context = useContext(XRechnungEInvoicingAddressFormContext)

  if (!context) {
    throw new Error('XRechnungEInvoicingAddressFormContextProvider is missing in the module!')
  }

  return context
}
